import type {
  SequelAiProposal,
  SequelAuthorVision,
  SequelPlanConfig,
  SequelPlanWorkflowStatus,
  SequelSeriesState,
  StoryBible,
} from '../types';

export const SEQUEL_AUTHOR_DIRECTION_BINDING = `AUTHOR DIRECTION IS BINDING unless it conflicts with established canon or safety constraints.
Do not silently replace, dilute, or contradict the author's requested focus, ending, exclusions, tone, or required events.`;

export const SEQUEL_PROMPT_PRIORITY_ORDER = [
  'Canon locks and hard continuity facts',
  'Explicit user must-not-happen rules',
  'Explicit user must-happen rules',
  'User author direction and desired ending',
  'Approved AI proposal',
  'Default Architect invention',
] as const;

export function createEmptyAuthorVision(): SequelAuthorVision {
  return {
    authorDirection: '',
    mainConflictDirection: '',
    focusCharacters: [],
    desiredEnding: '',
    themesAndEmotionalDirection: '',
    mustHappen: [],
    mustNotHappen: [],
    charactersToFeatureMore: [],
    charactersToFeatureLess: [],
    toneDirection: '',
    pacingDirection: '',
    newStoryElementsNotes: '',
    additionalInstructions: '',
  };
}

export function createAiProposalFromPremise(premise: StoryBible['premise']): SequelAiProposal {
  const wrap = (value: string) => ({
    value: value || '',
    source: 'ai' as const,
    status: 'pending' as const,
  });
  return {
    surface: wrap(premise.surface),
    tension: wrap(premise.tension),
    change: wrap(premise.change),
    approved: false,
    declinedExplicitly: false,
  };
}

export function createDefaultSequelPlan(bible: StoryBible): SequelPlanConfig {
  return {
    workflowStatus: 'DRAFT',
    authorVision: createEmptyAuthorVision(),
    aiProposal: createAiProposalFromPremise(bible.premise),
    storyStructure: { mode: 'chronovela_roadmap' },
    ledgerInjections: [],
  };
}

export function mergeSequelPlan(existing: SequelPlanConfig | undefined, bible: StoryBible): SequelPlanConfig {
  if (!existing) return createDefaultSequelPlan(bible);
  return {
    ...createDefaultSequelPlan(bible),
    ...existing,
    authorVision: { ...createEmptyAuthorVision(), ...existing.authorVision },
    aiProposal: {
      ...createAiProposalFromPremise(bible.premise),
      ...existing.aiProposal,
      surface: { ...createAiProposalFromPremise(bible.premise).surface, ...existing.aiProposal.surface },
      tension: { ...createAiProposalFromPremise(bible.premise).tension, ...existing.aiProposal.tension },
      change: { ...createAiProposalFromPremise(bible.premise).change, ...existing.aiProposal.change },
    },
    storyStructure: { mode: 'chronovela_roadmap', ...existing.storyStructure },
  };
}

export function getAcceptedProposalFields(proposal: SequelAiProposal): {
  surface?: string;
  tension?: string;
  change?: string;
} {
  if (proposal.declinedExplicitly) return {};
  const pick = (field: SequelAiProposal['surface']) => {
    if (field.status === 'rejected') return undefined;
    if (field.status === 'accepted' || field.status === 'edited' || field.status === 'pending') {
      return field.value.trim() || undefined;
    }
    return undefined;
  };
  if (!proposal.approved) return {};
  return {
    surface: pick(proposal.surface),
    tension: pick(proposal.tension),
    change: pick(proposal.change),
  };
}

export function applySequelPlanToBible(bible: StoryBible, plan: SequelPlanConfig): StoryBible {
  const accepted = getAcceptedProposalFields(plan.aiProposal);
  const nextPremise = {
    ...bible.premise,
    ...(accepted.surface ? { surface: accepted.surface } : {}),
    ...(accepted.tension ? { tension: accepted.tension } : {}),
    ...(accepted.change ? { change: accepted.change } : {}),
  };

  const structure = plan.storyStructure;
  let premadeRoadmap = bible.premadeRoadmap;
  if (structure.mode === 'paste_full' && structure.premadeRoadmap?.trim()) {
    premadeRoadmap = structure.premadeRoadmap.trim();
  } else if (structure.mode === 'partial_ideas' && structure.partialIdeas?.trim()) {
    premadeRoadmap = structure.partialIdeas.trim();
  } else if (structure.mode === 'lock_events' && structure.chapterAnchors?.length) {
    premadeRoadmap = structure.chapterAnchors
      .map((a) => `Chapter ${a.chapterNumber}${a.locked ? ' [LOCKED]' : ''}: ${a.requiredEvent}`)
      .join('\n');
  }

  let targetChapterCount = bible.targetChapterCount;
  if (structure.mode === 'first_five_only') {
    targetChapterCount = Math.min(5, bible.targetChapterCount || 5);
  }

  return {
    ...bible,
    premise: nextPremise,
    premadeRoadmap,
    targetChapterCount,
    sequelPlan: plan,
  };
}

export function buildSequelPlanningPromptAddendum(
  plan: SequelPlanConfig | undefined,
  seriesState?: SequelSeriesState | null,
): string {
  if (!plan) return '';

  const v = plan.authorVision;
  const lines: string[] = [
    SEQUEL_AUTHOR_DIRECTION_BINDING,
    '',
    'SEQUEL PLANNING PRIORITY (highest to lowest):',
    ...SEQUEL_PROMPT_PRIORITY_ORDER.map((item, i) => `${i + 1}. ${item}`),
    '',
  ];

  if (seriesState?.canonLocks.length) {
    lines.push('CANON LOCKS:', ...seriesState.canonLocks.map((lock) => `- ${lock}`), '');
  }
  if (v.mustNotHappen.length) {
    lines.push('MUST NOT HAPPEN:', ...v.mustNotHappen.map((rule) => `- ${rule}`), '');
  }
  if (v.mustHappen.length) {
    lines.push('MUST HAPPEN:', ...v.mustHappen.map((rule) => `- ${rule}`), '');
  }
  if (v.authorDirection.trim()) lines.push(`AUTHOR DIRECTION:\n${v.authorDirection.trim()}`, '');
  if (v.mainConflictDirection.trim()) lines.push(`MAIN CONFLICT DIRECTION:\n${v.mainConflictDirection.trim()}`, '');
  if (v.desiredEnding.trim()) lines.push(`DESIRED ENDING:\n${v.desiredEnding.trim()}`, '');
  if (v.focusCharacters.length) lines.push(`FOCUS CHARACTERS: ${v.focusCharacters.join(', ')}`, '');
  if (v.charactersToFeatureMore.length) {
    lines.push(`FEATURE MORE: ${v.charactersToFeatureMore.join(', ')}`, '');
  }
  if (v.charactersToFeatureLess.length) {
    lines.push(`FEATURE LESS: ${v.charactersToFeatureLess.join(', ')}`, '');
  }
  if (v.toneDirection.trim()) lines.push(`TONE DIRECTION: ${v.toneDirection.trim()}`, '');
  if (v.pacingDirection.trim()) lines.push(`PACING DIRECTION: ${v.pacingDirection.trim()}`, '');
  if (v.themesAndEmotionalDirection.trim()) {
    lines.push(`THEMES / EMOTIONAL DIRECTION:\n${v.themesAndEmotionalDirection.trim()}`, '');
  }
  if (v.newStoryElementsNotes.trim()) {
    lines.push(`NEW STORY ELEMENT NOTES:\n${v.newStoryElementsNotes.trim()}`, '');
  }
  if (v.additionalInstructions.trim()) {
    lines.push(`ADDITIONAL INSTRUCTIONS:\n${v.additionalInstructions.trim()}`, '');
  }

  const accepted = getAcceptedProposalFields(plan.aiProposal);
  if (plan.aiProposal.approved && !plan.aiProposal.declinedExplicitly) {
    lines.push('APPROVED AI PROPOSAL (subordinate to author direction above):');
    if (accepted.surface) lines.push(`Proposed Surface Conflict: ${accepted.surface}`);
    if (accepted.tension) lines.push(`Proposed Underlying Tension: ${accepted.tension}`);
    if (accepted.change) lines.push(`Proposed Irreversible Change: ${accepted.change}`);
    lines.push('');
  } else if (plan.aiProposal.declinedExplicitly) {
    lines.push('AI PROPOSAL: Explicitly declined by author. Do not use generated proposal fields.', '');
  }

  return lines.join('\n').trim();
}

const CONCEPT_PATTERNS = [
  /\b(new|ancient|forbidden|lost)\s+(system|order|realm|dimension|magic|power|institution|faction|species|creature|threat)\b/gi,
  /\b(the|a)\s+[A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+){0,3}\s+(Covenant|Order|Council|Empire|Legion|Veil|Blight|Protocol|Pact)\b/g,
];

export function extractNamedConceptCandidates(text: string): string[] {
  const found = new Set<string>();
  for (const pattern of CONCEPT_PATTERNS) {
    const matches = text.match(pattern) || [];
    matches.forEach((m) => found.add(m.trim()));
  }
  return Array.from(found);
}

export interface ConceptInflationReport {
  exceeded: boolean;
  detectedConcepts: string[];
  maxAllowed: number;
  message?: string;
}

export function validateConceptInflation(
  proposal: SequelAiProposal,
  maxMajorConcepts: number,
): ConceptInflationReport {
  const text = [proposal.surface.value, proposal.tension.value, proposal.change.value].join('\n');
  const detectedConcepts = extractNamedConceptCandidates(text);
  const exceeded = detectedConcepts.length > maxMajorConcepts;
  return {
    exceeded,
    detectedConcepts,
    maxAllowed: maxMajorConcepts,
    message: exceeded
      ? `AI proposal introduces ${detectedConcepts.length} major new concepts (limit ${maxMajorConcepts}). Review before approval.`
      : undefined,
  };
}

export function canAdvanceToCanonReview(seriesState: SequelSeriesState): boolean {
  return seriesState.loadStatus === 'READY';
}

export function canApproveVolumePlan(
  seriesState: SequelSeriesState,
  plan: SequelPlanConfig,
  bible: StoryBible,
): { ok: boolean; reason?: string } {
  if (seriesState.loadStatus !== 'READY') {
    return {
      ok: false,
      reason:
        'Chronovela could not load the final state of the previous volume. Review or repair the source volume before creating a sequel.',
    };
  }
  if (!bible.title?.trim()) return { ok: false, reason: 'Volume title is required.' };
  if (!bible.targetChapterCount || bible.targetChapterCount < 1) {
    return { ok: false, reason: 'Target chapter count must be at least 1.' };
  }
  if (!plan.authorVision.authorDirection.trim()) {
    return { ok: false, reason: 'Author Direction is required before approval.' };
  }
  if (!plan.aiProposal.approved && !plan.aiProposal.declinedExplicitly) {
    return { ok: false, reason: 'Accept or explicitly decline the AI Story Proposal before approval.' };
  }
  if (plan.workflowStatus !== 'CANON_REVIEWED') {
    return { ok: false, reason: 'Review inherited canon before approving the volume plan.' };
  }
  return { ok: true };
}

export function canCreateVolume(
  plan: SequelPlanConfig,
  seriesState: SequelSeriesState,
  bible: StoryBible,
): { ok: boolean; reason?: string } {
  if (seriesState.loadStatus !== 'READY') {
    return {
      ok: false,
      reason:
        'Chronovela could not load the final state of the previous volume. Review or repair the source volume before creating a sequel.',
    };
  }
  if (!bible.title?.trim()) return { ok: false, reason: 'Volume title is required.' };
  if (!bible.targetChapterCount || bible.targetChapterCount < 1) {
    return { ok: false, reason: 'Target chapter count must be at least 1.' };
  }
  if (!plan.authorVision.authorDirection.trim()) {
    return { ok: false, reason: 'Author Direction is required before creating the volume.' };
  }
  if (!plan.aiProposal.approved && !plan.aiProposal.declinedExplicitly) {
    return { ok: false, reason: 'Accept or explicitly decline the AI Story Proposal before creating the volume.' };
  }
  if (plan.workflowStatus !== 'APPROVED') {
    return { ok: false, reason: 'Approve the volume plan before creating the volume.' };
  }
  return { ok: true };
}

export function nextWorkflowStatus(current: SequelPlanWorkflowStatus, action: string): SequelPlanWorkflowStatus {
  switch (action) {
    case 'proposal_generated':
      return 'PROPOSAL_GENERATED';
    case 'canon_reviewed':
      return 'CANON_REVIEWED';
    case 'approved':
      return 'APPROVED';
    case 'creating':
      return 'CREATING';
    case 'created':
      return 'CREATED';
    default:
      return current;
  }
}
